/**
 * Accessibility audit pricing — per page, with volume tiers. One table, read
 * by the services site's calculator and by the API when it prices a request
 * and when it raises the invoice, so the number a customer saw is the number
 * they are billed for the same page count.
 *
 * Also exported from the './services-pricing' source subpath: apps/web and
 * apps/services need the runtime values, and Vite cannot statically analyse
 * this package's CJS dist for them (see package.json "//").
 *
 * $10 per page, $50 minimum order (set 2026-10-14).
 */

export const SERVICES_CURRENCY = 'USD';

export interface PricingTier {
  /** First page count this tier applies to (inclusive). */
  fromPages: number;
  /** Last page count, inclusive; null = no upper bound. */
  toPages: number | null;
  /** Price per page, in whole currency units. */
  ratePerPage: number;
  label: string;
}

/**
 * Flat per-page pricing: one rate, every page, no volume tiers. Kept as a
 * one-entry tier list so a volume tier can be added later without changing
 * the quote shape.
 */
const FLAT_TIER: PricingTier = {
  fromPages: 1,
  label: 'Any number of pages',
  ratePerPage: 10,
  toPages: null,
};

export const PRICING_TIERS: readonly PricingTier[] = [FLAT_TIER];

/** Smallest billable audit, whatever the page count. */
export const MINIMUM_ORDER = 50;

/** Largest page count the calculator and intake form accept; beyond it the
 *  site asks the customer to talk to us. */
export const MAX_SELF_SERVE_PAGES = 1000;

/**
 * Time from payment to the full report. A range, because it depends on
 * scope: the site and emails quote the label with the disclaimer, and the
 * admin's due date uses the upper bound.
 */
export const REPORT_TURNAROUND_MAX_HOURS = 48;
export const REPORT_TURNAROUND_LABEL = '24–48 hours';
export const REPORT_TURNAROUND_SHORT = '24–48h';
export const TURNAROUND_DISCLAIMER =
  'Timing depends on scope. Larger or more complex audits can take longer, and we confirm the timeline when we agree scope.';

/** How long a badge stays valid after the audit date. */
export const BADGE_VALIDITY_MONTHS = 12;

export interface PriceQuote {
  pages: number;
  tier: PricingTier;
  ratePerPage: number;
  /** pages × rate, before the minimum. */
  subtotal: number;
  /** What is billed: max(subtotal, MINIMUM_ORDER). */
  total: number;
  minimumApplied: boolean;
  currency: string;
}

export function tierFor(pages: number): PricingTier {
  const tier = PRICING_TIERS.find(
    (t) => pages >= t.fromPages && (t.toPages === null || pages <= t.toPages),
  );
  return tier ?? FLAT_TIER;
}

export function priceFor(pages: number): PriceQuote {
  const n = Math.max(1, Math.floor(pages));
  const tier = tierFor(n);
  const subtotal = n * tier.ratePerPage;
  const total = Math.max(subtotal, MINIMUM_ORDER);
  return {
    currency: SERVICES_CURRENCY,
    minimumApplied: total > subtotal,
    pages: n,
    ratePerPage: tier.ratePerPage,
    subtotal,
    tier,
    total,
  };
}

export function formatMoney(amount: number, currency = SERVICES_CURRENCY) {
  return new Intl.NumberFormat('en-US', {
    currency,
    maximumFractionDigits: 0,
    style: 'currency',
  }).format(amount);
}
